import { z } from 'zod';
import { EstimateVersionStatusSchema } from '../lib/enums';
import { zDateOnly, zEmail, zIsoDateTime, zUuid } from './common';
import { SignupResponseSchema } from './auth';
import { RenderModelSchema } from './proposals';
/**
 * Public token endpoints (`/api/v1/public/*`) — served on the public DB role,
 * rate-limited. The tokenized single-document proposal link and the sub
 * invite claim are the ONLY sanctioned tenant-boundary crossings here.
 */
// -------------------------------------------------------------- proposal view
export const PublicProposalStateSchema = z.object({
    versionStatus: EstimateVersionStatusSchema,
    /** Consent already recorded for this render (unlocks the sign step). */
    consentGiven: z.boolean(),
    /** False once responded, expired, or superseded. */
    canRespond: z.boolean(),
    response: z
        .object({
        kind: z.enum(['accepted', 'declined']),
        signerName: z.string().nullable(),
        respondedAt: zIsoDateTime,
    })
        .nullable(),
    tokenExpiresAt: zIsoDateTime.nullable(),
});
/** GET /public/proposals/:token (records a `viewed` event). */
export const PublicProposalResponseSchema = z.object({
    document: RenderModelSchema,
    state: PublicProposalStateSchema,
});
// ------------------------------------------------------------------- consent
/** POST /public/proposals/:token/consent — explicit ESIGN consent. */
export const PublicConsentRequestSchema = z
    .object({
    agree: z.literal(true),
})
    .strict();
export const PublicConsentResponseSchema = z.object({
    ok: z.literal(true),
    consentGivenAt: zIsoDateTime,
});
// -------------------------------------------------------------- accept/decline
/**
 * POST /public/proposals/:token/accept — ESIGN intent: explicit checkbox
 * (`intentConfirmed`) + typed name signature. Attribution (ip/ua/time) is
 * captured server-side; integrity via hash binding to the render document.
 */
export const PublicAcceptRequestSchema = z
    .object({
    name: z.string().min(1).max(200),
    email: zEmail,
    typedSignature: z.string().min(1).max(200),
    /** Selected alternate section ids (server enforces one per alternate group). */
    alternates: z.array(zUuid).max(50).default([]),
    intentConfirmed: z.literal(true),
})
    .strict();
export const PublicAcceptResponseSchema = z.object({
    ok: z.literal(true),
    responseId: zUuid,
    signedAt: zIsoDateTime,
    /** Stays valid after token expiry for accepted proposals (retention). */
    signedCopyUrl: z.string(),
});
export const PublicDeclineRequestSchema = z
    .object({
    reason: z.string().min(1).max(2000),
    name: z.string().max(200).optional(),
    email: zEmail.optional(),
})
    .strict();
export const PublicDeclineResponseSchema = z.object({
    ok: z.literal(true),
    respondedAt: zIsoDateTime,
});
// ---------------------------------------------------------------- sub invites
/** GET /public/invites/:token — pending scope-package invite summary. */
export const PublicInviteResponseSchema = z.object({
    inviterCompanyName: z.string(),
    packageTitle: z.string(),
    scopeDescription: z.string(),
    dueDate: zDateOnly.nullable(),
    invitedEmail: zEmail,
    /** True when the invite was revoked or already claimed. */
    claimable: z.boolean(),
});
/**
 * POST /public/invites/:token/claim — creates the sub company + owner user
 * and binds the pending grant to the new company (the sanctioned seam entry).
 */
export const PublicInviteClaimRequestSchema = z
    .object({
    company: z
        .object({
        name: z.string().min(1).max(200),
    })
        .strict(),
    user: z
        .object({
        name: z.string().max(200).optional(),
        email: zEmail,
        password: z.string().min(10).max(200),
    })
        .strict(),
})
    .strict();
export const PublicInviteClaimResponseSchema = SignupResponseSchema;
//# sourceMappingURL=public.js.map